import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import {
  Library as LibraryIcon, Target, ArrowRight, Crown, Clock, ShieldCheck,
  MapPin, GraduationCap, CheckCircle2,
} from "lucide-react";

const AVAILABLE = [
  { id: "ca-life", name: "California Life", state: "California", license: "Life" },
  { id: "ca-ah", name: "California Accident & Health", state: "California", license: "Accident & Health" },
  { id: "ca-personal", name: "California Personal Lines", state: "California", license: "Personal Lines" },
  { id: "tx-pc", name: "Texas Property & Casualty", state: "Texas", license: "Property & Casualty" },
  { id: "tx-lh", name: "Texas Life & Health", state: "Texas", license: "Life & Health" },
];

function readinessLabel(r) {
  if (r == null) return "Not started";
  if (r >= 85) return "Exam Ready";
  if (r >= 70) return "On Track";
  if (r >= 40) return "Developing";
  return "Just Starting";
}

export default function Library() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [dash, setDash] = useState(null);
  const [stateFilter, setStateFilter] = useState("All");
  const [licenseFilter, setLicenseFilter] = useState("All");

  useEffect(() => {
    api.get("/workbook").then((r) => setChapters(r.data)).catch(() => {});
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  const full = user?.has_full_access;
  const completed = chapters.filter((c) => c.completed).length;
  const total = chapters.length;
  const progressPct = total ? Math.round((completed / total) * 100) : 0;
  const readiness = dash?.readiness;
  const continueChapter = useMemo(
    () => chapters.find((c) => !c.completed && !c.locked) || chapters.find((c) => !c.locked) || chapters[0],
    [chapters]
  );
  const expires = user?.access_expires_at ? new Date(user.access_expires_at).toLocaleDateString() : null;

  const goContinue = () => { if (continueChapter) navigate(`/workbook/${continueChapter.id}`); else navigate("/workbook"); };

  const states = ["All", ...new Set(AVAILABLE.map((c) => c.state))];
  const licenses = ["All", ...new Set(AVAILABLE.map((c) => c.license))];
  const filtered = AVAILABLE.filter(
    (c) => (stateFilter === "All" || c.state === stateFilter) && (licenseFilter === "All" || c.license === licenseFilter)
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <LibraryIcon className="h-7 w-7 text-amber-600" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">My Library</h1>
        </div>
        <p className="text-slate-500 mb-8">Your courses and everything you can add as Insurance License Lab grows.</p>

        {/* MY COURSES */}
        <h2 className="font-heading text-lg font-bold text-[#0A192F] mb-4">My Courses</h2>
        <div className="grid md:grid-cols-2 gap-5 mb-12" data-testid="library-my-courses">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col" data-testid="library-course-ca-pc">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> California</span>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5" /> Property &amp; Casualty</span>
                </div>
                <h3 className="font-heading font-bold text-xl text-[#0A192F]">California Property &amp; Casualty</h3>
              </div>
              {full
                ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 shrink-0"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Full Access</Badge>
                : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 shrink-0">Free Sample</Badge>}
            </div>

            <div className="mt-5 space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Course Progress</span><span className="font-semibold text-[#0A192F]">{progressPct}%</span></div>
                <Progress value={progressPct} className="h-2" />
                <div className="text-xs text-slate-400 mt-1">{completed} of {total} chapters complete</div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-slate-600"><Target className="h-4 w-4 text-amber-600" /> Exam Readiness: <span className="font-semibold text-[#0A192F]">{readinessLabel(readiness)}</span></span>
              </div>
              {full && expires && (
                <div className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> {user?.access_type === "lifetime" ? "Lifetime access" : `Access through ${expires}`}</div>
              )}
            </div>

            <div className="mt-6 flex gap-2">
              <Button onClick={goContinue} className="bg-[#0A192F] hover:bg-[#1E293B] text-white" data-testid="library-continue-btn">
                Continue Course <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              {!full && (
                <Button variant="outline" className="border-amber-300 text-amber-700" onClick={() => navigate("/upgrade")} data-testid="library-upgrade-btn">
                  <Crown className="h-4 w-4 mr-1.5" /> Upgrade — $49.95/yr
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* AVAILABLE COURSES */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-heading text-lg font-bold text-[#0A192F]">Available Courses</h2>
          <div className="flex flex-wrap gap-2">
            <div className="flex gap-1" data-testid="library-state-filters">
              {states.map((s) => (
                <button key={s} onClick={() => setStateFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border transition ${stateFilter === s ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`} data-testid={`library-filter-state-${s.toLowerCase()}`}>{s}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1" data-testid="library-license-filters">
              {licenses.map((l) => (
                <button key={l} onClick={() => setLicenseFilter(l)} className={`text-xs px-3 py-1.5 rounded-full border transition ${licenseFilter === l ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`} data-testid={`library-filter-license-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-available-courses">
          {filtered.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={`library-available-${c.id}`}>
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.state}</span>
                <span className="text-slate-300">·</span>
                <span>{c.license}</span>
              </div>
              <h3 className="font-heading font-semibold text-[#0A192F] leading-snug">{c.name}</h3>
              <div className="mt-auto pt-4 flex items-center justify-between">
                <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                <Button size="sm" variant="ghost" disabled className="text-slate-400" data-testid={`library-enroll-${c.id}`}>Notify me</Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-sm text-slate-400 py-6">No courses match those filters yet.</div>}
        </div>

        <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-5 flex items-center gap-3 text-sm text-slate-600">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          More states and license types are on the way — the study method you learn in California carries over to every new course.
        </div>
      </div>
    </Layout>
  );
}
